import React, {useCallback, useEffect, useMemo, useState} from "react";
import JobRest from "../../../rest/JobRest.ts";
import JobWebsocket from "../../../websocket/JobWebsocket.ts";
import WebsocketAPISerde from "../../../types/WebsocketAPISerde.ts";
import {
    IActionUpdatedMessage,
    IActiveActionSetMessage,
    ICreateCalibrationMessage,
    ICutterAssignedMessage,
    ICutterDeletedMessage,
    ICutterStatusChangeMessage,
    ICutterUpdatedMessage,
    IDeleteCalibrationMessage,
    IDependenciesAnalyzedMessage,
    IJobStatusChangeMessage,
    IJobUpdatedMessage,
    IResetJobMessage,
    IStartJobMessage,
    IUpdateActionOrderMesssage,
    IUserInputReceivedMessage,
    IUserInputRequestMessage,
    IWebsocketAPIMessage,
    KNOWN_WEBSOCKET_API_MESSAGES
} from "../../../types/WebsocketAPITypes.ts";
import useWebSocket from "react-use-websocket";
import {useWebsocketNotifier} from "../../../hooks/useWebsocketNotifier/useWebsocketNotifier.tsx";
import {uniqBy} from "lodash";
import {IFeature} from "../../../types/models/Feature.ts";
import {IGaugeDedupe} from "../Dedupe.types.ts";
import {IPopulatedCutter, IPopulatedJob} from "../../../types/FrontendTypes.ts";
import {useWebsocketHandler} from "../../../components/WebsocketHandler/useWebsocketHandler.tsx";
import CutterRest from "../../../rest/CutterRest.ts";
import CutterWebsocket from "../../../websocket/CutterWebsocket.ts";
import {useNavigate} from "react-router-dom";
import {ICalibration} from "../../../types/models/Calibration.ts";
import CalibrationRest from "../../../rest/CalibrationRest.ts";
import CalibrationWebsocket from "../../../websocket/CalibrationWebsocket.ts";

export interface useCutJobApi {
    jobId: string
}

export function useCutJobApi(props: useCutJobApi) {
    const jobRest = useMemo(() => new JobRest(), []);
    const jobWebsocket = useMemo(() => new JobWebsocket(), []);
    const cutterRest = useMemo(() => new CutterRest(), []);
    const cutterWebsocket = useMemo(() => new CutterWebsocket(), []);
    const calibrationRest = useMemo(() => new CalibrationRest(), []);
    const calibrationWebsocket = useMemo(() => new CalibrationWebsocket(), []);

    const navigate = useNavigate();

    const [job, setJob] = React.useState<IPopulatedJob | null>(null);
    const [calibrations, setCalibrations] = useState<(ICalibration[] | null)>(null)
    const [cutters, setCutters] = useState<IPopulatedCutter[]>([]);
    const [activeActionId, setActiveActionId] = React.useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [lastErrorMessage, setLastErrorMessage] = useState<(string | null)>(null)
    const [features, setFeatures] = useState<(IFeature[] | null)>(null)

    const cutterUpdateHandler: { [key in KNOWN_WEBSOCKET_API_MESSAGES]?: (message: IWebsocketAPIMessage) => void } = {
        [KNOWN_WEBSOCKET_API_MESSAGES.CUTTER_STATUS_CHANGE]: (message: ICutterStatusChangeMessage) => {
            if (message.data.cutterId === job?.cutterId) {
                setJob((prevJob) => ({
                    ...prevJob,
                    cutter: {
                        ...prevJob.cutter,
                        status: message.data.status,
                        state: message.data.state
                    }
                }) satisfies IPopulatedJob);
            }
        },
        [KNOWN_WEBSOCKET_API_MESSAGES.CUTTER_UPDATED]: (message: ICutterUpdatedMessage) => {
            if (message.data.cutterId === job?.cutterId) {
                setJob((prevJob) => ({
                    ...prevJob,
                    cutter: {
                        ...prevJob.cutter,
                        name: message.data.name
                    }
                }) satisfies IPopulatedJob);
            }
        },
        [KNOWN_WEBSOCKET_API_MESSAGES.CUTTER_DELETED]: (message: ICutterDeletedMessage) => {
            if (message.data.cutterId === job?.cutterId) {
                setJob((prevJob) => ({
                    ...prevJob,
                    cutter: null,
                    cutterId: null
                }) satisfies IPopulatedJob);
            }
        }
    };

    const fetchCutters = useCallback(async () => {
        const res = await cutterRest.get();
        setCutters(res.data.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cutterRest]);

    useEffect(() => {
        fetchCutters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchCutters, job]);

    // Handling cutter websocket messages
    useWebsocketHandler({
        webSocketUrl: cutterWebsocket.getAllUpdates(),
        refetch: fetchCutters,
        messageHandler: cutterUpdateHandler
    });

    const fetchCalibrations = useCallback(async () => {
        if (!job || !job.calibrationIds || job.calibrationIds.length === 0) {
            setCalibrations([]);
            return;
        }

        Promise.all(
            job.calibrationIds.map(calibrationId => {
                return calibrationRest.get_calibrations({_id: calibrationId}).then(res => {
                    return res.data.data[0] as ICalibration;
                })
            })
        ).then(calibrations_res => {
            setCalibrations(calibrations_res);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.calibrationIds]);


    useEffect(() => {
        fetchCalibrations();
    }, [fetchCalibrations, calibrationRest]);

    const calibrationUpdateHandler: { [key in KNOWN_WEBSOCKET_API_MESSAGES]?: (message: IWebsocketAPIMessage) => void } = {
        [KNOWN_WEBSOCKET_API_MESSAGES.CREATE_CALIBRATION]: (message: ICreateCalibrationMessage) => {
            setJob((prevJob) => ({
                ...prevJob,
                calibrationIds: [...prevJob.calibrationIds, message.data.calibrationId]
            }) satisfies IPopulatedJob);
        },
        [KNOWN_WEBSOCKET_API_MESSAGES.DELETE_CALIBRATION]: (message: IDeleteCalibrationMessage) => {
            setJob((prevJob) => ({
                ...prevJob,
                calibrationIds: prevJob.calibrationIds.filter(id => id !== message.data.calibrationId)
            }) satisfies IPopulatedJob);
        }
    }

    useWebsocketHandler({
        webSocketUrl: calibrationWebsocket.getAllUpdates(),
        refetch : fetchCalibrations,
        messageHandler: calibrationUpdateHandler
    })

    const {sendMessage, lastMessage, readyState} = useWebSocket(
        jobWebsocket.getUpdatesByJobId(props.jobId!),
        {
            shouldReconnect: () => true,
            heartbeat: true
        });

    useWebsocketNotifier({readyState: readyState})

    function assignCutter(cutterId: string) {
        if (!job) {
            return
        }

        jobRest
            .assignCutter(job._id, cutterId)
            .then(async () => {
                const cutter = await cutterRest.getById(cutterId);
                setJob((prevJob) => ({
                    ...prevJob,
                    cutterId: cutterId,
                    cutter: cutter.data.data,
                }) satisfies IPopulatedJob);
            });
    }

    function onJobStatusChange(parsedMessage: IJobStatusChangeMessage) {
        setJob((prevJob) => {
            const newJob = {...prevJob, status: parsedMessage.data.status, statusReason: parsedMessage.data.reason};
            newJob.features.forEach(
                feature => {
                    feature.entityType = "feature";
                    feature.dependencies.forEach(g => g.entityType = "gauge");
                }
            );
            return newJob satisfies IPopulatedJob;
        });
    }

    function onJobUpdated(parsedMessage: IJobUpdatedMessage) {
        setJob((prevJob) => ({
            ...prevJob,
            title: parsedMessage.data.title,
        }) satisfies IPopulatedJob);
    }

    function onCutterAssigned(parsedMessage: ICutterAssignedMessage) {
        setJob((prevJob) => ({
            ...prevJob,
            cutterId: parsedMessage.data.cutter._id,
            cutter: parsedMessage.data.cutter,
        }) satisfies IPopulatedJob);
    }

    function onDependenciesAnalyzed(parsedMessage: IDependenciesAnalyzedMessage) {
        setJob((prevJob) => {
            const newJob = {...prevJob, actionOrder: undefined};
            newJob.features = parsedMessage.data.features;

            const groupingKeysActions = [];
            newJob.features = uniqBy(newJob.features, feat => feat.groupingKey).map(filteredFeature => {
                filteredFeature.dependencies = filteredFeature?.dependencies?.map(dep => {
                    const newDep = {...dep, keep: false} as IGaugeDedupe;
                    if (!groupingKeysActions.includes(dep.groupingKey)) {
                        newDep.keep = true;
                        groupingKeysActions.push(dep.groupingKey);
                    }
                    return newDep;
                });
                return filteredFeature;
            });
            return newJob satisfies IPopulatedJob;
        });
    }

    function onUpdateActionOrder(parsedMessage: IUpdateActionOrderMesssage) {
        setJob((prevJob) => ({
            ...prevJob,
            actionOrder: parsedMessage.data.actionOrder
        } satisfies IPopulatedJob));
    }

    function onActiveActionSet(parsedMessage: IActiveActionSetMessage) {
        setActiveActionId(parsedMessage.data.actionId)
        setIsSubmitting(false)
        setLastErrorMessage(null)
    }

    function onUserInputRequest(parsedMessage: IUserInputRequestMessage) {
        setIsSubmitting(false)
        const index = job.actionOrder.findIndex(value => {
            return value._id === parsedMessage.data.actionId
        })
        const newFlattenedActionList = [...job.actionOrder];
        if (parsedMessage.data.lastErrorMessage) {
            setLastErrorMessage(parsedMessage.data.lastErrorMessage)
        }
        if (index >= 0) {
            newFlattenedActionList[index].isLoading = false

            setJob((prevJob) => ({
                ...prevJob,
                actionOrder: newFlattenedActionList
            } satisfies IPopulatedJob));
        } else {
            console.warn("Could not stop loading. Could not find corresponding id.", parsedMessage.data.actionId)
        }
    }

    function onActionUpdated(parsedMessage: IActionUpdatedMessage) {
        const index = job.actionOrder?.findIndex(value => value._id === parsedMessage.data._id)
        const newFlattenedActionList = [...job.actionOrder];
        console.log("Action updated!", index)
        if (index >= 0) {
            console.log("Updated Action", newFlattenedActionList[index], parsedMessage.data)
            newFlattenedActionList[index] = parsedMessage.data

            setJob((prevJob) => ({
                ...prevJob,
                actionOrder: newFlattenedActionList
            } satisfies IPopulatedJob));
        } else {
            console.warn("Could not stop loading. Could not find corresponding id.")
        }
    }

    function onJobReset(parsedMessage: IResetJobMessage) {
        navigate("/job/" + parsedMessage.data.newJobId)
    }

    useEffect(() => {
        if (!lastMessage) {
            return
        }

        try {
            const message = WebsocketAPISerde.decode(lastMessage.data);
            if (!job) {
                console.warn("Job is not set")
                return;
            }
            switch (message.type) {
                case KNOWN_WEBSOCKET_API_MESSAGES.JOB_STATUS_CHANGE: {
                    const parsedMessage = message as IJobStatusChangeMessage;
                    onJobStatusChange(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.CUTTER_ASSIGNED: {
                    const parsedMessage = message as ICutterAssignedMessage;
                    onCutterAssigned(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.DEPENDENCIES_ANALYZED: {
                    const parsedMessage = message as IDependenciesAnalyzedMessage;
                    onDependenciesAnalyzed(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.RESET_JOB: {
                    const parsedMessage = message as IResetJobMessage;
                    onJobReset(parsedMessage);
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.JOB_UPDATED: {
                    const parsedMessage = message as IJobUpdatedMessage;
                    onJobUpdated(parsedMessage);
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.JOB_STARTED:
                    console.warn("Not implemented - job started")
                    break;
                case KNOWN_WEBSOCKET_API_MESSAGES.CREATE_JOB:
                    console.warn("Not implemented - Create Job")
                    break;
                case KNOWN_WEBSOCKET_API_MESSAGES.USER_INPUT_RECEIVED:
                    console.warn("Not implemented - UserInputReceived")
                    break;
                case KNOWN_WEBSOCKET_API_MESSAGES.UPDATE_ACTION_ORDER: {
                    const parsedMessage = message as IUpdateActionOrderMesssage;
                    onUpdateActionOrder(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.ACTIVE_ACTION_SET: {
                    // {"type":"ACTIVE_ACTION_SET","data":{"actionId":"3a6ada62-4268-4383-a251-520b3f6f4618"}}
                    const parsedMessage = message as IActiveActionSetMessage;
                    onActiveActionSet(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.USER_INPUT_REQUEST: {
                    const parsedMessage = message as IUserInputRequestMessage;
                    onUserInputRequest(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.ACTIVE_ACTION_UNSET: {
                    console.warn("Not implemented - active action unset")
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.ACTION_UPDATED: {
                    const parsedMessage = message as IActionUpdatedMessage;
                    onActionUpdated(parsedMessage)
                    break;
                }
                case KNOWN_WEBSOCKET_API_MESSAGES.GAUGE_UPDATED:
                    console.warn("Not implemented - gauge updated")
                    break;
                case KNOWN_WEBSOCKET_API_MESSAGES.LOG:
                    console.warn("Not implemented - log updates")
                    break;
                case KNOWN_WEBSOCKET_API_MESSAGES.PONG:
                    break;
                default:
                    console.warn("Did not interpret type", message.type, message)
            }
        } catch
            (e) {
            console.error("Failed to parse Message sent by cutterManager", e, lastMessage)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);

    useEffect(() => {
        if (!props.jobId) {
            console.warn("Did not have jobId. Aborting")
            return;
        }
        jobRest
            .getById(props.jobId)
            .then((response) => {
                if (!response.data.data) {
                    throw new Error("No valid JobID given")
                }
                const newJob = response.data.data as IPopulatedJob
                if (newJob?.lastActiveActionId) {
                    setActiveActionId(newJob?.lastActiveActionId)
                }
                const groupingKeysActions: string[] = [];

                newJob.features = uniqBy(newJob.features, feat => feat.groupingKey).map(filteredFeature => {
                    filteredFeature.dependencies = filteredFeature?.dependencies?.map(dep => {
                        const newDep = {...dep, keep: false} as IGaugeDedupe
                        if (!groupingKeysActions.includes(dep.groupingKey)) {
                            newDep.keep = true
                            groupingKeysActions.push(dep.groupingKey)
                        }
                        newDep.entityType = "gauge";
                        return newDep
                    });
                    filteredFeature.entityType = "feature";
                    return filteredFeature;
                });

                setJob(newJob);
            })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.jobId]);

    useEffect(() => {
        jobRest
            .getFeaturesByJobId(props.jobId)
            .then(response => {
                setFeatures(response.data.data)
            })
    }, [props.jobId, jobRest]);

    async function handleUserInputSubmit(answeredInput: (object | null)) {

        if (!job || !activeActionId) {
            console.error("No current user action specified")
            return
        }
        setIsSubmitting(true)

        const message = WebsocketAPISerde.encode({
            type: KNOWN_WEBSOCKET_API_MESSAGES.USER_INPUT_RECEIVED,
            data: {
                jobId: job._id,
                actionId: activeActionId,
                response: answeredInput ?? {}
            }
        } satisfies IUserInputReceivedMessage)

        sendMessage(message)
    }

    function handleStart() {

        if (!props.jobId) {
            console.warn("Cannot start job. JobId is not given")
            return
        }

        const payload = WebsocketAPISerde.encode(
            {
                type: KNOWN_WEBSOCKET_API_MESSAGES.START_JOB,
                data: {
                    jobId: props.jobId,
                }
            } satisfies IStartJobMessage
        )

        console.log("Sending", payload)
        sendMessage(payload)
    }

    return {
        handleUserInputSubmit,
        handleStart,
        isSubmitting,
        activeActionId,
        job,
        assignCutter,
        cutters,
        lastErrorMessage,
        features,
        calibrations,
        fetchCalibrations,
    }

}